// HomeGoalCard — Sika goal card matching the prototype's GoalCard.
//
// Bound to useSikaGoals(). Picks the most recently updated active goal
// (or the first if none are active). When no goal exists, renders the
// prototype's "No goal set yet" variant — copy-faithful.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../../components/icons/Icon.tsx";
import { useSikaGoals, type SikaGoalRow } from "../../sika/useSikaGoals.ts";

function pickPrimaryGoal(goals: readonly SikaGoalRow[]): SikaGoalRow | null {
  if (goals.length === 0) return null;
  const active = goals.find((g) => g.status === "active");
  return active ?? goals[0]!;
}

function pct(current: number, target: number): number {
  if (!Number.isFinite(target) || target <= 0) return 0;
  const v = (current / target) * 100;
  if (v < 0) return 0;
  if (v > 100) return 100;
  return Math.round(v);
}

function fmtMoney(v: number, ccy: string): string {
  if (!Number.isFinite(v)) return "—";
  const prefix = ccy === "GHS" || ccy === "GH₵" ? "GH₵" : ccy + " ";
  return `${prefix}${Math.round(v).toLocaleString()}`;
}

function fmtPct(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return `${Math.round(v * 100)}%`;
}

export function HomeGoalCard(): JSX.Element {
  const { goals, loading } = useSikaGoals();
  const goal = useMemo(() => pickPrimaryGoal(goals), [goals]);

  if (loading && goal === null) {
    return (
      <div className="home-card">
        <div className="card-head">
          <div className="h-title">Sika · Your goal</div>
          <div className="h-actions">
            <Link className="link" to="/sika">
              Open Sika <Icon name="chev" size={12} />
            </Link>
          </div>
        </div>
        <div className="goal">
          <div className="gt">Loading…</div>
        </div>
      </div>
    );
  }

  if (goal === null) {
    return (
      <div className="home-card">
        <div className="card-head">
          <div className="h-title">Sika · Your goal</div>
          <div className="h-actions">
            <Link className="link" to="/sika">
              Open Sika <Icon name="chev" size={12} />
            </Link>
          </div>
        </div>
        <div className="goal">
          <div className="gt">No goal set yet</div>
          <div className="gname">What are you working toward?</div>
          <p
            style={{
              fontSize: 13,
              color: "var(--text-secondary)",
              marginTop: 0,
              marginBottom: 14,
            }}
          >
            Tell Sika in plain English — &ldquo;buy a laptop&rdquo;, &ldquo;school fees&rdquo;,
            &ldquo;travel money&rdquo;. She&rsquo;ll turn it into a weekly chain you can follow.
          </p>
          <Link
            to="/sika"
            className="home-btn primary"
            style={{ width: "100%", justifyContent: "center" }}
          >
            Talk to Sika
          </Link>
          <div className="goal-foot">
            <div className="sika-mark">SK</div>
            Sika simulator · free tier
          </div>
        </div>
      </div>
    );
  }

  const target = goal.target_amount;
  const saved = goal.current_amount;
  const progress = pct(saved, target);
  const probability = fmtPct(goal.full_chain_probability);
  const downside = fmtMoney(goal.opening_stake ?? 0, goal.currency);
  const totalWeeks = goal.timeline_weeks ?? 0;
  const weekHit = goal.week_target_hit ?? 0;

  return (
    <div className="home-card">
      <div className="card-head">
        <div className="h-title">Sika · Your goal</div>
        <div className="h-actions">
          <Link className="link" to="/sika">
            Edit plan <Icon name="chev" size={12} />
          </Link>
        </div>
      </div>
      <div className="goal">
        <div className="gt">
          {goal.status === "active"
            ? totalWeeks > 0
              ? `In progress · ${String(weekHit)} of ${String(totalWeeks)} weeks`
              : "In progress"
            : goal.status === "achieved"
              ? "Achieved"
              : "Pending"}
        </div>
        <div className="gname">{goal.title}</div>
        <div className="goal-bar-wrap">
          <div className="goal-bar">
            <div className="fill" style={{ width: `${String(progress)}%` }} />
          </div>
          <div className="goal-bar-meta">
            <span>
              <span className="saved">{fmtMoney(saved, goal.currency)}</span> of{" "}
              {fmtMoney(target, goal.currency)}
            </span>
            <span>{String(progress)}%</span>
          </div>
        </div>
        <div className="goal-stats">
          <div className="goal-stat">
            <div className="k">Cadence</div>
            <div className="v">{goal.recommended_cadence}</div>
          </div>
          <div className="goal-stat">
            <div className="k">Probability</div>
            <div className="v">{probability}</div>
          </div>
          <div className="goal-stat">
            <div className="k">Downside</div>
            <div className="v">{downside}</div>
          </div>
        </div>
        <div className="goal-foot">
          <div className="sika-mark">SK</div>
          Plan tracked by Sika.
        </div>
      </div>
    </div>
  );
}
